<script>
  import {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
  } from '@headlessui/vue';
  import { mapActions, mapState } from 'pinia';
  import { userStore } from '@/store/user';
  import { cartStore } from '@/store/cart';
  import { promoStore } from '@/store/promo';
  import UserImage from '@/components/users/profile/UserImage.vue';
  import Toggle from '@/components/form/Toggle.vue';
  import EmailCheckComponent from '@/components/EmailCheckComponent.vue';
  import { platformStore } from '@/store/platform';
  import { toCurrency } from '@/utils/math';
  export default {
    name: 'CheckoutModal',
    components: {
      Dialog,
      DialogPanel,
      TransitionChild,
      TransitionRoot,
      UserImage,
      Toggle,
      EmailCheckComponent,
    },
    props: {
      open: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['close', 'closeAndClear'],
    data() {
      return {
        agreeToTerms: false,
        step: 1,
        urls: window.viploge.urls,
        saving: false,
        checkout: null,
        order: null,
        cartError: null,
        emailStatus: null,
        guestData: {
          email: '',
          firstName: '',
          lastName: '',
          wantsAccount: false,
        },
        // Promocode related data
        promoCode: '',
        promoApplied: false,
        appliedPromo: null,
        promoError: null,
        checkingPromo: false,
      };
    },
    computed: {
      ...mapState(userStore, ['currentUser']),
      ...mapState(cartStore, ['cart']),
      ...mapState(platformStore, ['currentPlatform']),
      sortedTickets() {
        const ticketsByEvents = {};
        if (this.cart.ticket && this.cart.ticket.length > 0) {
          this.cart.ticket.forEach((ticket) => {
            const eventKey = 'event_' + ticket.event.id;
            if (!ticketsByEvents[eventKey]) {
              ticketsByEvents[eventKey] = {
                event: ticket.event,
                tickets: [],
                total: 0,
              };
            }
            ticketsByEvents[eventKey].tickets.push(ticket.ticket);
            ticketsByEvents[eventKey].total += ticket.ticket.price;
          });
        }
        return Object.values(ticketsByEvents);
      },
      subtotalAmount() {
        let total = 0;
        this.sortedTickets.forEach((event) => {
          total += event.total;
        });
        return total;
      },
      subtotal() {
        return toCurrency(this.subtotalAmount);
      },
      promoDiscount() {
        if (!this.promoApplied || !this.appliedPromo) return 0;

        if (this.appliedPromo.isPercent) {
          return (this.subtotalAmount / 100) * this.appliedPromo.amount;
        } else {
          // For fixed amount discounts, ensure we don't discount more than the total
          return Math.min(this.appliedPromo.amount, this.subtotalAmount);
        }
      },
      totalAmount() {
        return Math.max(0, this.subtotalAmount - this.promoDiscount);
      },
      total() {
        return toCurrency(this.totalAmount);
      },
      canProceedAsGuest() {
        const canProceed =
          this.emailStatus &&
          (this.emailStatus.available ||
            this.emailStatus.user_type === 'guest') &&
          this.guestData.email &&
          (this.guestData.firstName || this.emailStatus.user_type === 'guest'); // Allow existing guests without firstName

        return canProceed;
      },
      isExistingGuest() {
        return this.emailStatus && this.emailStatus.user_type === 'guest';
      },
    },
    mounted() {
      this.resetCheckout();
    },
    methods: {
      ...mapActions(userStore, ['hasRight']),
      ...mapActions(cartStore, ['submitOrder']),
      ...mapActions(promoStore, ['validatePromocode']),
      resetCheckout() {
        this.agreeToTerms = false;
        this.emailStatus = null;
        this.guestData = {
          email: '',
          firstName: '',
          lastName: '',
          wantsAccount: false,
        };
        // Reset promocode data
        this.promoCode = '';
        this.promoApplied = false;
        this.appliedPromo = null;
        this.promoError = null;
        this.checkingPromo = false;

        this.checkout = {
          name: '',
          email: '',
          userId: null,
          phone: '',
          cart: null,
          isPaid: false,
          emailRecipient: '',
          orderOnBehalfOf: '',
          total: 0,
        };
        if (this.currentUser) {
          this.checkout.userId = this.currentUser.id;
          this.checkout.name = this.currentUser.fullName;
          this.checkout.email = this.currentUser.email;
          this.checkout.emailRecipient = this.currentUser.email;
          this.step = 2;
        }
      },
      confirmOrder() {
        this.saving = true;

        // V2 checkout - only send necessary data, no cart manipulation needed
        // Include promocode information for UserOrder
        this.checkout.promoCode = this.promoApplied
          ? this.appliedPromo.code
          : null;
        this.submitOrder(this.checkout)
          .then((res) => {
            // V2 checkout response structure
            if (res.data.success) {
              const order = res.data.order;
              const payment = res.data.payment;

              // If payment checkout URL is provided, redirect to payment
              if (payment && payment.checkoutUrl) {
                location.href = payment.checkoutUrl;
              } else if (order.status === 'paid') {
                // Order is already paid (free tickets or admin marked as paid)
                this.order = order;
                this.step = 4;
              } else {
                // No payment needed or something went wrong
                this.step = 3;
              }
            } else {
              // Error occurred
              this.cartError = res.data.error || 'Er is een fout opgetreden';
              this.step = 5;
            }
            this.saving = false;
          })
          .catch((error) => {
            // Handle network or server errors
            this.cartError =
              error.response?.data?.error ||
              'Er is een fout opgetreden bij het verwerken van uw bestelling';
            this.step = 5;
            this.saving = false;
          });
      },
      showPrice(price) {
        return toCurrency(price);
      },
      handleEmailChecked(data) {
        this.emailStatus = data.status;
        this.guestData = {
          email: data.email,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          wantsAccount: data.wantsAccount || false,
        };
      },
      handleShowLogin(data) {
        // Redirect to login page with return URL
        window.location.href = `${
          this.urls.login
        }?goto=home&email=${encodeURIComponent(data.email)}`;
      },
      handlePasswordReset(data) {
        // Redirect to password reset page
        window.location.href = `${
          this.urls.password_reset
        }?email=${encodeURIComponent(data.email)}`;
      },
      handleGuestDataUpdate(data) {
        this.guestData = {
          email: data.email,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          wantsAccount: data.wantsAccount || false,
        };

        // If this is an existing guest user, automatically proceed to checkout
        // For existing guests, we can proceed even without firstName if we have email
        if (
          this.emailStatus &&
          this.emailStatus.user_type === 'guest' &&
          data.email
        ) {
          this.proceedAsGuest();
        }
      },
      async proceedAsGuest() {
        if (!this.canProceedAsGuest) {
          return;
        }

        // If user is an existing guest, handle upgrade or proceed directly
        if (this.emailStatus.user_type === 'guest') {
          this.checkout.email = this.guestData.email;
          // Use name from guest data, or fallback to email prefix if no name available
          const fullName =
            `${this.guestData.firstName} ${this.guestData.lastName}`.trim();
          this.checkout.name = fullName || this.guestData.email.split('@')[0];
          this.checkout.emailRecipient = this.guestData.email;
          this.checkout.userId = null;

          // If guest wants account upgrade, we need to handle that
          if (this.guestData.wantsAccount) {
            // TODO: Show UserUpgradeModal for existing guest upgrade
            // For now, proceed normally and handle upgrade later
          }

          this.step = 2;
          return;
        }

        try {
          // Create new guest user through Enhanced User System
          const response = await fetch('/api/guest/create', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: this.guestData.email,
              firstName: this.guestData.firstName,
              lastName: this.guestData.lastName,
            }),
          });

          const result = await response.json();

          if (response.ok && result.success) {
            // Update checkout data with guest user info
            this.checkout.email = this.guestData.email;
            this.checkout.name =
              `${this.guestData.firstName} ${this.guestData.lastName}`.trim();
            this.checkout.emailRecipient = this.guestData.email;
            this.checkout.userId = null; // Guest user, no platform user ID yet

            // Move to next step
            this.step = 2;
          } else {
            // Handle error - user might already exist
            if (response.status === 409) {
              // User already exists, proceed anyway with existing data
              this.checkout.email = this.guestData.email;
              this.checkout.name =
                `${this.guestData.firstName} ${this.guestData.lastName}`.trim();
              this.checkout.emailRecipient = this.guestData.email;
              this.checkout.userId = null;
              this.step = 2;
            } else {
              console.error('Failed to create guest user:', result.error);
              alert(
                'Er ging iets mis bij het aanmaken van uw gastaccount. Probeer het opnieuw.'
              );
            }
          }
        } catch (error) {
          console.error('Error creating guest user:', error);
          alert(
            'Er ging iets mis bij het aanmaken van uw gastaccount. Probeer het opnieuw.'
          );
        }
      },
      async checkPromoCode() {
        if (!this.promoCode.trim()) return;

        this.checkingPromo = true;
        this.promoError = null;

        try {
          const result = await this.validatePromocode(this.promoCode.trim());

          if (result.success) {
            this.promoError = null;
            this.promoApplied = true;
            this.appliedPromo = result.promo;
            this.promoCode = result.promo.code; // Keep the code visible
          } else {
            this.promoError = result.error;
            this.promoApplied = false;
            this.appliedPromo = null;
          }
        } catch {
          this.promoError =
            'Er ging iets mis bij het valideren van de promocode';
          this.promoApplied = false;
          this.appliedPromo = null;
        }

        this.checkingPromo = false;
      },
      removePromoCode() {
        this.promoCode = '';
        this.promoApplied = false;
        this.appliedPromo = null;
        this.promoError = null;
      },
      getEventImage(event) {
        // Try to get banner image from project assets first
        if (event.project?.assets) {
          const bannerAsset = event.project.assets.find(
            (asset) => asset.type === 'banner'
          );
          if (bannerAsset?.url) {
            return bannerAsset.url;
          }

          // Fallback to square image
          const squareAsset = event.project.assets.find(
            (asset) => asset.type === 'square'
          );
          if (squareAsset?.url) {
            return squareAsset.url;
          }
        }

        // Fallback to event banner if available
        if (event.banner?.url) {
          return event.banner.url;
        }

        return null;
      },
    },
  };
</script>

<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-[60]">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-y-auto rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 max-h-[90vh]"
            >
              <form @submit.prevent="confirmOrder">
                <div v-if="step === 1">
                  <h2 class="mb-2 block border-b border-gray-200 pb-2">
                    Mijn gegevens
                  </h2>
                  <div class="flex space-x-4">
                    <div class="flex-1 rounded border border-gray-200 p-2">
                      <h4>Aanmelden als gast</h4>
                      <div class="mb-2 text-sm text-gray-400">
                        Indien u van plan bent maar eenmalig tickets te
                        bestellen. U krijgt uw tickets via mail, maar kan geen
                        bestellingen downloaden.
                      </div>

                      <!-- Enhanced Email Check Component -->
                      <EmailCheckComponent
                        @email-checked="handleEmailChecked"
                        @show-login="handleShowLogin"
                        @show-password-reset="handlePasswordReset"
                        @guest-data-updated="handleGuestDataUpdate"
                      />

                      <!-- Continue button - always visible, disabled when not ready -->
                      <button
                        type="button"
                        :disabled="!canProceedAsGuest"
                        class="mt-4 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow transition-all focus:outline-0 focus:ring-2 focus:ring-offset-2 sm:text-sm"
                        :class="
                          canProceedAsGuest
                            ? 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 cursor-pointer'
                            : 'cursor-not-allowed bg-gray-400 opacity-50'
                        "
                        @click="canProceedAsGuest && proceedAsGuest()"
                      >
                        Volgende stap
                      </button>
                    </div>
                    <div class="flex-1 rounded border border-gray-200 p-2">
                      <h4>Ik wil me aanmelden</h4>
                      <div class="mb-2 text-sm text-gray-400">
                        Indien u reeds een account heeft of graag een wil. Het
                        voordeel van een account is dat u nadien uw bestellingen
                        en tickets op 1 handige plaats kan terugvinden.
                      </div>
                      <div class="flex flex-col space-y-5 pt-8">
                        <div>Ik heb een account en</div>
                        <a
                          :href="`${urls.login}?goto=home`"
                          class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow focus:outline-0 focus:ring-2 focus:ring-offset-2 sm:text-sm"
                        >
                          Ik wil nu aanmelden
                        </a>
                        <div>Ik heb jammer genoeg geen account, maar</div>
                        <a
                          :href="`${urls.register}?goto=home`"
                          class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow focus:outline-0 focus:ring-2 focus:ring-offset-2 sm:text-sm"
                        >
                          Ik wil er nu een maken
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="step === 2">
                  <h2 class="mb-2 block border-b border-gray-200 pb-2">
                    Overzicht van mijn bestelling
                  </h2>
                  <div
                    class="mb-2 flex sm:flex-col gap-3 border-b border-gray-200 pb-2 sm:flex-row sm:flex-nowrap sm:items-start sm:justify-between sm:gap-4"
                  >
                    <div class="flex items-center gap-3 sm:shrink-0">
                      <UserImage :user="checkout" height="h-12" width="w-12" />
                      <div>
                        <div
                          class="text-xl font-bold text-gray-900 sm:text-2xl"
                        >
                          {{ checkout.name }}
                        </div>
                        <div class="text-sm font-medium text-gray-600">
                          {{ checkout.email }}
                        </div>
                      </div>
                    </div>
                    <div
                      class="flex flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-4"
                    >
                      <div
                        class="flex items-center gap-2 text-sm font-medium text-gray-600 sm:min-w-[220px]"
                      >
                        <span>{{ $t('checkout.send_tickets_to') }}:</span>
                        <input
                          v-model="checkout.emailRecipient"
                          class="input !py-1 !text-stone-800 w-full"
                          type="email"
                          :placeholder="$t('checkout.email_placeholder')"
                        />
                      </div>
                      <div
                        v-if="hasRight('ticket_manager')"
                        class="flex items-center gap-2 text-sm font-medium text-gray-600 sm:min-w-[220px]"
                      >
                        <span>{{ $t('checkout.order_on_behalf_of') }}:</span>
                        <input
                          v-model="checkout.orderOnBehalfOf"
                          class="input !py-1 !text-stone-800 w-full"
                          type="text"
                          :placeholder="
                            $t('checkout.order_on_behalf_of_placeholder')
                          "
                        />
                      </div>
                    </div>
                  </div>
                  <div class="h-48 overflow-y-auto md:h-80">
                    <div
                      v-for="(event, eventIndex) in sortedTickets"
                      :key="`event-${eventIndex}`"
                      class="mb-2 items-end border-b border-dashed border-gray-200 pb-2 md:flex"
                    >
                      <!-- Event image (banner or square fallback) -->
                      <div
                        v-if="getEventImage(event.event)"
                        class="mr-3 hidden md:block"
                      >
                        <img
                          :src="getEventImage(event.event)"
                          :alt="event.event.title"
                          class="h-16 w-24 rounded object-cover"
                        />
                      </div>
                      <div class="flex-1">
                        <div class="font-bold">{{ event.event.title }}</div>
                        <div class="text-sm text-gray-400">
                          {{ event.event.date }} | {{ event.event.time }}
                        </div>
                      </div>
                      <div class="mt-1 flex md:mt-0">
                        <div class="md:w-20">
                          {{ event.tickets.length }} tickets
                        </div>
                        <div class="text-right font-bold md:w-32">
                          {{ showPrice(event.total) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Promocode section -->
                  <div class="border-t border-gray-200 py-2">
                    <div class="flex items-center space-x-2">
                      <div class="flex-1">
                        <input
                          v-model="promoCode"
                          type="text"
                          class="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-md border border-gray-300 p-2 text-gray-900 shadow focus:border sm:text-sm"
                          :class="[
                            {
                              'pointer-events-none bg-gray-50 opacity-40':
                                promoApplied,
                            },
                          ]"
                          placeholder="Promocode"
                          :readonly="promoApplied"
                          @keyup.enter="checkPromoCode"
                        />
                      </div>
                      <button
                        v-if="!promoApplied"
                        type="button"
                        :disabled="!promoCode || checkingPromo"
                        class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium text-white shadow focus:outline-0 focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                        @click="checkPromoCode"
                      >
                        <span v-if="!checkingPromo">Toepassen</span>
                        <svg
                          v-if="checkingPromo"
                          class="h-4 w-4 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                          ></circle>
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </button>
                      <button
                        v-if="promoApplied"
                        type="button"
                        class="focus:ring-primary-500 inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow hover:bg-gray-50 focus:outline-0 focus:ring-2 focus:ring-offset-2"
                        @click="removePromoCode"
                      >
                        Verwijderen
                      </button>
                    </div>
                    <div v-if="promoError" class="mt-1 text-sm text-red-600">
                      {{ promoError }}
                    </div>
                    <div
                      v-if="promoApplied && appliedPromo"
                      class="mt-1 text-sm text-green-600"
                    >
                      Promocode "{{ appliedPromo.code }}" toegepast:
                      {{
                        appliedPromo.isPercent
                          ? appliedPromo.amount + '%'
                          : '€' + appliedPromo.amount
                      }}
                      korting
                    </div>
                  </div>
                  <div
                    class="justify-between border-b border-t border-gray-200 py-3 font-bold md:flex"
                  >
                    <div>
                      <Toggle
                        v-if="hasRight('ticket_manager')"
                        title="Deze bestelling is betaald"
                        :checked="checkout.isPaid"
                        @input="checkout.isPaid = $event"
                      />
                    </div>
                    <div class="mt-2 flex flex-1 flex-col items-end md:mt-0">
                      <div
                        v-if="promoApplied && promoDiscount > 0"
                        class="text-sm text-gray-600"
                      >
                        Subtotaal: {{ subtotal }}
                      </div>
                      <div
                        v-if="promoApplied && promoDiscount > 0"
                        class="text-sm text-green-600"
                      >
                        Korting: -{{ showPrice(promoDiscount) }}
                      </div>
                      <div>Totaal: {{ total }}</div>
                    </div>
                  </div>
                </div>
                <div v-if="step === 3">
                  <h2 class="mb-2 block border-b border-gray-200 pb-2">
                    Bedankt voor je bestelling!
                  </h2>
                  <div class="mt-8 h-80 text-gray-400">
                    <b>Er is geen openstaande betaling meer gevonden.</b>
                    <br />
                    <br />
                    Je bestelling is geregistreerd.
                    <br />
                    Van deze bestelling ontvang je nog een bevestigingsmail
                    (kijk zeker ook je spam na). In de bevestigingsmail kan je
                    ook nalezen wanneer de tickets verstuurd worden. Heb je
                    binnen de 2 uur geen bevestigingsmail ontvangen, neem dan
                    contact op met
                    <a
                      class="text-primary-600 hover:text-primary-800"
                      :href="`mailto:${currentPlatform.ticketsMailtoAddress}`"
                    >
                      {{ currentPlatform.ticketsMailtoAddress }}
                    </a>
                    .
                  </div>
                </div>
                <div v-if="step === 4">
                  <h2 class="mb-2 block border-b border-gray-200 pb-2">
                    Bedankt voor je bestelling!
                  </h2>
                  <div class="my-8 text-gray-600">
                    <div
                      class="mb-4 flex items-center gap-2 rounded-md bg-green-50 p-3 text-green-700"
                    >
                      <svg
                        class="h-5 w-5 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <span>
                        Je bestelling is bevestigd en gratis verwerkt.
                      </span>
                    </div>
                    Je tickets worden zo snel mogelijk verstuurd naar
                    <strong>{{ order && order.emailRecipient }}</strong>
                    .
                    <br />
                    <br />
                    Kijk zeker ook je spam na. Heb je binnen de 2 uur geen
                    bevestigingsmail ontvangen, neem dan contact op met
                    <a
                      v-if="
                        currentPlatform && currentPlatform.ticketsMailtoAddress
                      "
                      class="text-primary-600 hover:text-primary-800"
                      :href="`mailto:${currentPlatform.ticketsMailtoAddress}`"
                    >
                      {{ currentPlatform.ticketsMailtoAddress }}
                    </a>
                    <span v-else>de organisator</span>
                    .
                  </div>
                </div>
                <div v-if="step === 5">
                  <h2 class="mb-2 block border-b border-gray-200 pb-2">
                    Je bestelling kon niet worden geregistreerd!
                  </h2>
                  <div class="text-grey-600 my-8">
                    <div class="text-red-500">
                      {{ cartError }}
                    </div>

                    <br />
                    <br />
                    Heb je nog vragen, neem dan contact op met
                    <a
                      class="text-primary-600 hover:text-primary-800"
                      :href="`mailto:${currentPlatform.ticketsMailtoAddress}`"
                    >
                      {{ currentPlatform.ticketsMailtoAddress }}
                    </a>
                  </div>
                </div>
                <div
                  v-if="step === 2"
                  class="mt-5 flex w-full flex-col justify-between gap-3 sm:mt-4 sm:flex-row sm:gap-0"
                >
                  <div class="mb-3 flex items-center gap-1">
                    <label
                      for="checkout-terms"
                      class="!flex cursor-pointer items-center gap-2"
                    >
                      <input
                        id="checkout-terms"
                        v-model="agreeToTerms"
                        type="checkbox"
                        class="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
                      />
                      <span>Ik ga akkoord met de</span>
                    </label>
                    <a
                      href="/nl/voorwaarden"
                      class="text-primary-600 hover:text-primary-800"
                      target="_blank"
                    >
                      verkoopsvoorwaarden
                    </a>
                  </div>
                  <div
                    class="flex w-full sm:flex-col justify-end gap-2 sm:w-auto sm:flex-row"
                  >
                    <button
                      type="button"
                      class="focus:ring-primary-500 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow hover:text-gray-500 focus:outline-0 focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm"
                      @click="$emit('close')"
                    >
                      {{ $t('generic.act.cancel') }}
                    </button>
                    <button
                      type="submit"
                      class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex w-full justify-center space-x-2 rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow focus:outline-0 focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm"
                      :class="[{ 'opacity-40': !agreeToTerms }]"
                      :disabled="saving || !agreeToTerms"
                    >
                      <span v-if="!checkout.isPaid">Bestelling betalen</span>
                      <span v-if="checkout.isPaid">Bestelling bevestigen</span>
                      <svg
                        v-if="saving"
                        class="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        ></circle>
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
                <div
                  v-if="step < 3 && step !== 2"
                  class="mt-5 flex justify-end sm:mt-4"
                >
                  <button
                    type="button"
                    class="focus:ring-primary-500 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow hover:text-gray-500 focus:outline-0 focus:ring-2 focus:ring-offset-2 sm:text-sm"
                    @click="$emit('close')"
                  >
                    {{ $t('generic.act.cancel') }}
                  </button>
                </div>
                <div
                  v-if="step === 3 || step === 4 || step === 5"
                  class="mt-5 flex justify-end sm:mt-4"
                >
                  <button
                    v-if="step === 3 || step === 4"
                    type="button"
                    class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow focus:outline-0 focus:ring-2 focus:ring-offset-2 sm:text-sm"
                    @click="$emit('closeAndClear')"
                  >
                    Sluiten
                  </button>
                  <button
                    v-if="step === 5"
                    type="button"
                    class="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow focus:outline-0 focus:ring-2 focus:ring-offset-2 sm:text-sm"
                    @click="$emit('closeAndClear')"
                  >
                    Begin opnieuw
                  </button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
